"use client";
import { useEffect, useState } from "react";
import KpiCard from "@/components/dashboard/KpiCard";
import StatusBadge from "@/components/dashboard/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  HardDrive, DollarSign, TrendingDown, BarChart3, Search,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import { getAnalyticsSummary, type AnalyticsSummary } from "@/services/api/client";

const revenueByPlan = [
  { plan: "Starter", revenue: 1200 },
  { plan: "Business", revenue: 3200 },
  { plan: "Enterprise", revenue: 4020 },
];

const allTenants = [
  { name: "Banque Léman SA", plan: "Enterprise", storage: "3.2 TB", mrr: "€890", status: "success" as const, statusLabel: "Active" },
  { name: "SwissSecure AG", plan: "Business", storage: "1.8 TB", mrr: "€590", status: "success" as const, statusLabel: "Active" },
  { name: "MedTech Lyon", plan: "Starter", storage: "450 GB", mrr: "€190", status: "warning" as const, statusLabel: "Trial" },
  { name: "Alpine Pharma GmbH", plan: "Enterprise", storage: "2.1 TB", mrr: "€890", status: "pending" as const, statusLabel: "Paused" },
  { name: "Genève Finance SA", plan: "Business", storage: "1.4 TB", mrr: "€590", status: "success" as const, statusLabel: "Active" },
  { name: "Zurich Holdings", plan: "Enterprise", storage: "4.1 TB", mrr: "€1,290", status: "success" as const, statusLabel: "Active" },
];

const Admin = () => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);

  useEffect(() => {
    void getAnalyticsSummary()
      .then((data) => setAnalytics(data))
      .catch(() => setAnalytics(null));
  }, []);

  const platformKpis = [
    { title: "Page Views", value: String(analytics?.pageViews ?? 0), icon: HardDrive, subtitle: "Home, Book, Event" },
    { title: "Unique Page Views", value: String(analytics?.uniquePageViews ?? 0), icon: DollarSign, subtitle: "Distinct visitor/page" },
    { title: "Booking Started", value: String(analytics?.bookingStarted ?? 0), icon: TrendingDown, subtitle: "Booking flow opened" },
    { title: "Bookings Booked", value: String(analytics?.bookingsBooked ?? 0), icon: BarChart3, subtitle: "Meetings confirmed" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t("admin.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("admin.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {platformKpis.map((kpi) => (
          <KpiCard key={kpi.title} {...kpi} />
        ))}
      </div>

      <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
        <h3 className="text-sm font-medium text-foreground mb-4">{t("admin.revenueByPlan")}</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={revenueByPlan}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 91%)" />
            <XAxis dataKey="plan" tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} />
            <Tooltip formatter={(value: number) => `€${value}`} />
            <Bar dataKey="revenue" fill="hsl(210, 100%, 19%)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-lg border border-border/50 bg-card shadow-sm">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <h3 className="text-sm font-medium">{t("admin.allTenants")}</h3>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder={t("admin.searchTenants")} className="pl-8 h-8 w-48 text-sm" />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">{t("admin.tenant")}</TableHead>
              <TableHead className="text-xs">{t("admin.plan")}</TableHead>
              <TableHead className="text-xs">{t("admin.storage")}</TableHead>
              <TableHead className="text-xs">MRR</TableHead>
              <TableHead className="text-xs">{t("admin.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allTenants.map((tenant) => (
              <TableRow key={tenant.name}>
                <TableCell className="text-sm font-medium">{tenant.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{tenant.plan}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{tenant.storage}</TableCell>
                <TableCell className="text-sm font-medium">{tenant.mrr}</TableCell>
                <TableCell><StatusBadge status={tenant.status} label={tenant.statusLabel} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Admin;
